import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { GlossaryBlueprint } from "@/types-games";
import {
  createTemplate,
  updateTemplate,
} from "@/lib/stores/templatesStore";
import { getGlossaryItemsUsingBlueprint } from "@/lib/stores/objectsStore";
import { BlueprintData } from "@/components/wizards/blueprints/types";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";

export interface UseBlueprintsHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
  setIsEditModalOpen: (value: boolean) => void;
  setSelectedBlueprint: (value: GlossaryBlueprint | null) => void;
}

export function useBlueprintsHandlers({
  data,
  selectedGame,
  config,
  setIsEditModalOpen,
  setSelectedBlueprint,
}: UseBlueprintsHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this item";

      // Check if blueprint is in use
      const itemsUsingBlueprint = getGlossaryItemsUsingBlueprint(id, gameId);

      if (itemsUsingBlueprint.length > 0) {
        const confirmDialog = confirmation({
          title: "Template in Use",
          description: (
            <div>
              <p className="mb-3">
                This template is currently being used by{" "}
                {itemsUsingBlueprint.length} glossary item(s). Deleting it may
                cause issues with those items.
              </p>
              <div className="max-h-32 overflow-y-auto">
                <p className="text-sm font-medium mb-2">
                  Items using this template:
                </p>
                {itemsUsingBlueprint.map((glossaryItem) => (
                  <div
                    key={glossaryItem.id}
                    className="text-sm text-gray-600 py-1 px-2 bg-gray-50 rounded mb-1"
                  >
                    {glossaryItem.name}
                  </div>
                ))}
              </div>
            </div>
          ),
          confirmText: "Delete Anyway",
          cancelText: "Cancel",
          variant: "destructive",
          onConfirm: async () => {
            try {
              await config.handleDelete(id, gameId, toast);
              confirmDialog.dismiss();
            } catch (error) {
              console.error("Error deleting template:", error);
              confirmDialog.dismiss();
              toast({
                title: "Error",
                description: "Failed to delete template",
                variant: "destructive",
              });
            }
          },
          onCancel: () => {
            confirmDialog.dismiss();
          },
        });
        return;
      }

      // Normal delete flow
      const confirmDialog = confirmation({
        title: "Delete Template",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await config.handleDelete(id, gameId, toast);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting template:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete template",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, config, confirmation, toast]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/templates/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(() => {
    // Navigate to new template page instead of opening modal
    navigate('/templates/new');
  }, [navigate]);

  const handleSaveBlueprint = useCallback(
    async (blueprintData: {
      id?: string;
      game_id?: string;
      name: string;
      description: string;
      tags: string[];
      type: string;
      settings: Record<string, unknown>;
      data: {
        sections: Record<string, unknown>;
        devnotes: Record<string, unknown>;
        elementOrder: string[];
      };
      version?: {
        changes: Record<string, unknown>;
        version: number;
      };
    }) => {
      try {
        if (blueprintData.id && blueprintData.id.startsWith("temp-")) {
          await createTemplate({
            game_id: blueprintData.game_id || selectedGame?.id || "",
            name: blueprintData.name,
            tags: blueprintData.tags,
            type: blueprintData.type,
            description: blueprintData.description,
            data: blueprintData.data as unknown as BlueprintData,
            settings: blueprintData.settings,
            version: blueprintData.version,
          });
          toast({
            title: "Success",
            description: "Template created successfully",
          });
        } else {
          if (!blueprintData.id) {
            throw new Error("Blueprint ID is required for updates");
          }

          await updateTemplate({
            id: blueprintData.id,
            game_id: blueprintData.game_id || selectedGame?.id || "",
            name: blueprintData.name,
            tags: blueprintData.tags,
            type: blueprintData.type,
            description: blueprintData.description,
            data: blueprintData.data as unknown as BlueprintData,
            settings: blueprintData.settings,
            version: blueprintData.version,
          });
          toast({
            title: "Success",
            description: "Template updated successfully",
          });
        }

        setIsEditModalOpen(false);
        setSelectedBlueprint(null);
      } catch (error) {
        console.error("Error saving blueprint:", error);
        toast({
          title: "Error",
          description: "Failed to save template",
          variant: "destructive",
        });
      }
    },
    [selectedGame, toast, setIsEditModalOpen, setSelectedBlueprint]
  );

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
    handleSaveBlueprint,
  };
}

